/**
 * Notification Utilities
 *
 * Shared utilities for notification components
 */

import { TFunction } from 'i18next';
import { Notification, NotificationScope, NotificationType } from '@/types/notification';
import { buildBoardReleaseHref, buildPortfolioReleaseHref } from '@/utils/releaseLinks';
import type { ReleaseBoardColumnKey } from '@/types/releaseBoard';

/**
 * Get icon class name for notification type
 * Returns HappyIcon class name (without 'hm-' prefix)
 */
export function getNotificationIcon(type: NotificationType): string {
  switch (type) {
    case 'POST_PUBLISHED':
      return 'tick-circle';
    case 'POST_FAILED':
      return 'cross-circle';
    case 'POST_SCHEDULED':
      return 'calendar';
    case 'APPROVAL_REQUESTED':
      return 'review';
    case 'APPROVAL_APPROVED':
      return 'tick';
    case 'APPROVAL_RESUBMITTED':
      return 'repeat';
    case 'APPROVAL_WITHDRAWN':
      return 'refresh';
    case 'APPROVAL_REJECTED':
      return 'cross';
    case 'APPROVAL_CHANGES_REQUESTED':
      return 'pencil';
    case 'APPROVAL_COMMENT':
    case 'COMMENT_ADDED':
      return 'comment-circle';
    case 'MENTION':
    case 'APPROVAL_MENTION':
      return 'chat-bubble';
    case 'CHANNEL_DISCONNECTED':
      return 'plug';
    case 'CHANNEL_EXPIRING':
      return 'caution';
    case 'TEAM_INVITATION':
      return 'user-plus';
    case 'TEAM_MEMBER_JOINED':
      return 'group';
    case 'BULK_IMPORT_COMPLETED':
      return 'upload';
    case 'BULK_IMPORT_FAILED':
      return 'cross-circle';
    case 'CANVA_IMPORT_COMPLETED':
      return 'image';
    case 'CANVA_IMPORT_FAILED':
      return 'cross-circle';
    case 'BLOG_SOCIAL_DRAFTS_CREATED':
      return 'blog-content';
    case 'BLOG_SOCIAL_GENERATION_FAILED':
      return 'cross-circle';
    case 'VARIATIONS_AUTO_TRANSLATED':
      return 'language-change';
    case 'SYSTEM':
    default:
      return 'bell';
  }
}

/**
 * Get color class for notification type
 */
export function getNotificationColor(type: NotificationType): string {
  switch (type) {
    case 'POST_PUBLISHED':
    case 'APPROVAL_APPROVED':
    case 'BULK_IMPORT_COMPLETED':
    case 'CANVA_IMPORT_COMPLETED':
    case 'BLOG_SOCIAL_DRAFTS_CREATED':
      return 'text-primary-700';
    case 'POST_FAILED':
    case 'APPROVAL_REJECTED':
    case 'CHANNEL_DISCONNECTED':
    case 'BULK_IMPORT_FAILED':
    case 'CANVA_IMPORT_FAILED':
    case 'BLOG_SOCIAL_GENERATION_FAILED':
      return 'text-red-600';
    case 'APPROVAL_CHANGES_REQUESTED':
    case 'CHANNEL_EXPIRING':
      return 'text-amber-700';
    case 'APPROVAL_REQUESTED':
    case 'APPROVAL_RESUBMITTED':
    case 'APPROVAL_WITHDRAWN':
    case 'APPROVAL_COMMENT':
    case 'APPROVAL_MENTION':
    case 'COMMENT_ADDED':
    case 'MENTION':
    case 'POST_SCHEDULED':
    case 'TEAM_INVITATION':
    case 'TEAM_MEMBER_JOINED':
      return 'text-primary-700';
    case 'VARIATIONS_AUTO_TRANSLATED':
      return 'text-primary-700';
    default:
      return 'text-sage-gray';
  }
}

/** Keep the bell and the full list in sync, including system alert variants. */
export function getNotificationVisual(notification: Notification): { icon: string; color: string } {
  const alertType = typeof notification.data?.alertType === 'string' ? notification.data.alertType : '';
  if (notification.type === 'SYSTEM_ANNOUNCEMENT' && alertType.startsWith('carte_blanche_')) {
    return { icon: 'shield', color: 'text-primary-700' };
  }
  if (notification.type === 'SYSTEM_ANNOUNCEMENT' && alertType) {
    return { icon: 'caution', color: 'text-amber-700' };
  }
  return { icon: getNotificationIcon(notification.type), color: getNotificationColor(notification.type) };
}

/**
 * Get navigation link for notification
 */
export function getNotificationLink(
  type: NotificationType,
  postId?: number,
  channelId?: number,
  variationId?: number
): string {
  if (type === 'VARIATIONS_AUTO_TRANSLATED') {
    if (postId) return `/creator?editPostId=${postId}`;
    return '/creator';
  }
  if (type === 'CANVA_IMPORT_COMPLETED' || type === 'CANVA_IMPORT_FAILED') {
    return '/creator';
  }
  if (type === 'BLOG_SOCIAL_DRAFTS_CREATED' || type === 'BLOG_SOCIAL_GENERATION_FAILED') {
    return '/creator';
  }
  if (type.startsWith('APPROVAL_')) {
    const isChatType = type === 'APPROVAL_COMMENT' || type === 'APPROVAL_MENTION';
    return buildBoardReleaseHref({ postId, variationId, tab: postId && isChatType ? 'comments' : undefined });
  }
  if (postId) {
    return `/posts/${postId}`;
  }
  if (channelId) {
    return '/channels';
  }
  return '#';
}

function numFrom(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && /^\d+$/.test(value)) return Number(value);
  return undefined;
}

/** Bundled notifications ("N items ready") carry metadata.bundleCount and no single post. */
export function getBundleCount(metadata: Record<string, unknown> | undefined): number | undefined {
  const n = numFrom(metadata?.bundleCount);
  return n !== undefined && n > 0 ? n : undefined;
}

/** Reminder notifications: type APPROVAL_REQUESTED + isReminder, or a dedicated type. */
function getReminderStage(type: string, meta: Record<string, unknown>): 'tomorrow' | 'today' | null {
  const isReminderType = type.includes('REMINDER');
  if (!isReminderType && meta.isReminder !== true) return null;
  return meta.reminderStage === 'today' ? 'today' : 'tomorrow';
}

/** The board column a bundled release notification is about ("N items ready for your release" = In review ...). */
function bundleBoardColumn(type: string): ReleaseBoardColumnKey | null {
  switch (type) {
    case 'APPROVAL_REQUESTED':
    case 'APPROVAL_RESUBMITTED':
      return 'in_review';
    case 'APPROVAL_CHANGES_REQUESTED':
      return 'changes_requested';
    case 'APPROVAL_APPROVED':
      return 'released';
    default:
      return null;
  }
}

/**
 * Resolve the full link for a notification (single source for bell, page and SSE).
 *
 * - In a brand: the Release Board / drawer inside that brand, or the post.
 * - In the Brand Portfolio (scope 'portfolio'): a deep link into the portfolio that opens
 *   the row's brand and, for release items, the release drawer.
 */
export function getNotificationHref(notification: Notification, scope: NotificationScope = 'team'): string {
  const meta = notification.data ?? {};
  const postId = numFrom(meta.postId) ?? notification.post?.id;
  const channelId = numFrom(meta.channelId) ?? notification.channel?.id;
  const variationId = numFrom(meta.variationId);
  const teamId = notification.teamId ?? numFrom(meta.teamId);
  const type = notification.type;

  const isReleaseItem =
    type.startsWith('APPROVAL_') ||
    (type === 'POST_FAILED' && meta.reason === 'release_required');
  const isBundle = getBundleCount(meta) !== undefined && !postId;

  if (scope === 'portfolio' && teamId) {
    if (isReleaseItem) {
      return buildPortfolioReleaseHref({
        teamId,
        postId,
        variationId,
        openBoard: isBundle,
        column: isBundle ? bundleBoardColumn(type) : null,
        tab: type === 'APPROVAL_COMMENT' || type === 'APPROVAL_MENTION' ? 'comments' : undefined,
      });
    }
    return `/agency/portfolio?${new URLSearchParams({ openBrand: String(teamId) }).toString()}`;
  }

  if (isBundle && type.startsWith('APPROVAL_')) return buildBoardReleaseHref({ column: bundleBoardColumn(type) });
  if (type === 'POST_FAILED' && meta.reason === 'release_required') {
    return buildBoardReleaseHref({ postId, variationId });
  }
  return getNotificationLink(type, postId, channelId, variationId);
}

/**
 * Translate notification title and body based on type and metadata.
 * Falls back to original strings if translation key is missing.
 */
export function translateNotification(
  type: string,
  title: string,
  message: string,
  metadata: Record<string, unknown> | undefined,
  t: TFunction
): { title: string; body: string } {
  // SYSTEM_ANNOUNCEMENT and SYSTEM use dynamic admin text - don't translate
  if (type === 'SYSTEM_ANNOUNCEMENT' || type === 'SYSTEM') {
    return { title, body: message };
  }

  const meta = metadata || {};

  // Pre-process metadata for interpolation
  const interpolation: Record<string, unknown> = { ...meta };

  // Join channels array for POST_PUBLISHED
  if (Array.isArray(meta.channels)) {
    interpolation.channelList = (meta.channels as string[]).join(', ');
  }

  // Approval notifications: the backend sends a ready item label that already
  // includes the version; older notifications only have postTitle.
  const rawLabel = String(meta.itemLabel || meta.postTitle || '').trim();
  if (rawLabel) {
    interpolation.itemLabel = rawLabel;
  } else {
    // Post without a caption: the backend sends '' so the client translates.
    const untitled = String(t('untitled_post'));
    interpolation.itemLabel = meta.variationName
      ? `${untitled} · ${String(meta.variationName)}`
      : untitled;
  }

  // Determine keys - special cases
  let titleKey = `notif_title_${type}`;
  let bodyKey = `notif_body_${type}`;

  if (type === 'APPROVAL_COMMENT' && meta.isSticker === true) {
    bodyKey = meta.isReply
      ? 'notif_body_APPROVAL_COMMENT_STICKER_REPLY'
      : 'notif_body_APPROVAL_COMMENT_STICKER';
    if (meta.isReply) titleKey = 'notif_title_APPROVAL_COMMENT_REPLY';
  } else if (type === 'APPROVAL_COMMENT' && meta.isReply) {
    titleKey = 'notif_title_APPROVAL_COMMENT_REPLY';
    bodyKey = 'notif_body_APPROVAL_COMMENT_REPLY';
  }

  // Release-workflow variants. English defaults are used until the locale keys land.
  let defaultTitle = title;
  let defaultBody = message;
  const bundleCount = getBundleCount(meta);
  const reminderStage = getReminderStage(type, meta);
  if (bundleCount !== undefined) interpolation.count = bundleCount;

  if (reminderStage) {
    titleKey = `notif_title_APPROVAL_REMINDER_${reminderStage}`;
    bodyKey = `notif_body_APPROVAL_REMINDER_${reminderStage}`;
    defaultTitle = String(t(reminderStage === 'today' ? 'release_needed_today' : 'release_needed_tomorrow'));
    defaultBody = `${defaultTitle}: "{{itemLabel}}"`;
  } else if (type === 'APPROVAL_WITHDRAWN') {
    defaultTitle = 'Withdrawn from review';
    defaultBody = '{{requesterName}} withdrew "{{itemLabel}}" from review in {{teamName}}';
  } else if (type === 'APPROVAL_RESUBMITTED') {
    defaultTitle = 'Changes made';
    defaultBody = '{{requesterName}} made the changes on "{{itemLabel}}"';
  } else if (type === 'APPROVAL_REQUESTED' && bundleCount !== undefined) {
    titleKey = 'notif_title_APPROVAL_REQUESTED_BUNDLE';
    bodyKey = 'notif_body_APPROVAL_REQUESTED_BUNDLE';
    defaultTitle = 'Ready for your release';
    defaultBody = '{{count}} items are ready for your release · {{teamName}}';
  } else if (type === 'APPROVAL_APPROVED' && bundleCount !== undefined) {
    titleKey = 'notif_title_APPROVAL_APPROVED_BUNDLE';
    bodyKey = 'notif_body_APPROVAL_APPROVED_BUNDLE';
    defaultTitle = 'Released';
    defaultBody = '{{count}} items were released · {{teamName}}';
  } else if (type === 'VARIATIONS_AUTO_TRANSLATED' && numFrom(meta.count) !== undefined) {
    titleKey = 'notif_title_VARIATIONS_AUTO_TRANSLATED_REVIEW';
    bodyKey = 'notif_body_VARIATIONS_AUTO_TRANSLATED_REVIEW';
    defaultTitle = 'Translations ready to check';
    defaultBody = '{{count}} translations of "{{itemLabel}}" are ready to check';
  } else if (type === 'POST_PUBLISHED' && !Array.isArray(meta.channels)) {
    titleKey = 'notif_title_POST_PUBLISHED_LIVE';
    bodyKey = 'notif_body_POST_PUBLISHED_LIVE';
    defaultTitle = 'Went live';
    defaultBody = '"{{itemLabel}}" went live';
  } else if (type === 'POST_FAILED' && meta.reason === 'release_required') {
    titleKey = 'notif_title_POST_FAILED_RELEASE_REQUIRED';
    bodyKey = 'notif_body_POST_FAILED_RELEASE_REQUIRED';
    defaultTitle = 'Not published';
    defaultBody = '"{{itemLabel}}" wasn\'t published: it needs a release first';
  } else if (type === 'POST_FAILED' && meta.reason === 'team_inactive') {
    titleKey = 'notif_title_POST_FAILED_TEAM_INACTIVE';
    bodyKey = 'notif_body_POST_FAILED_TEAM_INACTIVE';
    defaultTitle = 'Not published';
    defaultBody = '"{{itemLabel}}" wasn\'t published because {{teamName}} is deactivated. You can reschedule it after the brand is reactivated.';
  }

  if (type === 'POST_FAILED' && meta.platform && meta.reason !== 'release_required' && meta.reason !== 'team_inactive') {
    bodyKey = 'notif_body_POST_FAILED_platform';
  }

  const translatedTitle = String(t(titleKey, { ...interpolation, defaultValue: defaultTitle }));
  const translatedBody = String(t(bodyKey, { ...interpolation, defaultValue: defaultBody }));

  // If interpolation variables remain unresolved (missing metadata), fall back to original
  return {
    title: translatedTitle.includes('{{') ? title : translatedTitle,
    body: translatedBody.includes('{{') ? message || defaultBody : translatedBody,
  };
}
